import { useTranslator } from "@eo-locale/react";

import { SyntheticEvent, useEffect } from "react";

import { identity, pickBy } from "lodash";

import { useRouter } from "next/router";

import { Box, Button, HTMLWrapper, VStack } from "@/storybook-components";

import { createUnverifiedUser } from "@/redux/features/user/thunk";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import QuestionFactory from "../questions/question-factory";

import { setError, updateUnverifiedUser } from "@/redux/features/user/slice";
import useEmbeds from "@/hooks/useEmbeds";
import usePinned from "@/hooks/usePinned";
import Alerts from "../alerts";

const UnverifiedUserForm = ({ tool }: { tool: string }) => {
  const { query, asPath } = useRouter();
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();

  const { status } = useAppSelector((state) => state.user);
  const { answers, errors } = useAppSelector((state) => state.questions);
  const { error } = useAppSelector((state) => state.user);
  const { projectId, surveyId } = query;
  const { isPinned } = usePinned();
  const { disableSignup } = useAppSelector((state) => state.settings.settings);

  useEffect(() => {
    if (answers["accountEmailQuestion"] || answers["loginQuestion"]) {
      dispatch(
        updateUnverifiedUser({
          login: answers["loginQuestion"] || "",
          email: answers["accountEmailQuestion"] || ""
        })
      );
    }
  }, [answers, dispatch]);

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    dispatch(createUnverifiedUser({ projectId: query.projectId as string }));
  };

  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  const getRoute = (route: "login" | "register") => {
    if (isPinned)
      return `/${route}?next=${encodeURIComponent(`/${projectId}?tool=survey_tool&tool_id=${surveyId}#tool_tab`)}`;

    if (isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl)
      return `/${route}`;

    return `/${route}?next=${encodeURIComponent(asPath)}`;
  };

  const questions = [
    {
      id: "accountEmailQuestion",
      type: "SignupForm::AccountEmailQuestion",
      question: translate("common.email")
    },
    {
      id: "loginQuestion",
      type: "SignupForm::LoginQuestion",
      question: translate("common.screenName")
    },
    {
      id: "tosQuestion",
      type: "SignupForm::TosQuestion",
      question: translate("common.termsAndConditions", {
        target: getRouteTarget()
      })
    }
  ];

  const hasAllQuestionAnswered =
    questions.length !== Object.values(pickBy(answers, identity)).length;

  return (
    <>
      <Box as="form" onSubmit={handleSubmit}>
        <VStack spacing={6} maxWidth="450px" align="flex-start">
          {questions.map((question) => (
            <QuestionFactory key={question.id} question={question} />
          ))}

          <HTMLWrapper
            html={`${translate("common.alternativelySignIn", {
              loginRoute: getRoute("login"),
              target: getRouteTarget()
            })}${
              disableSignup
                ? "."
                : "&nbsp;" +
                  translate("common.orRegister", {
                    registerRoute: getRoute("register"),
                    target: getRouteTarget()
                  })
            }`}
          />

          {!!error && (
            <Alerts
              status="error"
              maxWidth="450px"
              onClose={() => dispatch(setError(null))}
              title={translate("errors.sorrySomethingWentWrong")}
            />
          )}
        </VStack>

        <Button
          variant="primary"
          mt={4}
          size="lg"
          type="submit"
          isLoading={status === "saving"}
          width={{ base: "full", md: "auto" }}
          isDisabled={
            hasAllQuestionAnswered || Object.values(errors).length > 0
          }
        >
          {translate("common.continue")}
        </Button>
      </Box>
    </>
  );
};

export default UnverifiedUserForm;
